/* TSI Predictive Search Dropdown
 * Panel direkt unter der Suchleiste (Kopf und Suchzeile auf Tablet und Handy).
 * Seit TSI - 4.3.1 wie bei LinkedIn: beim Fokus mit leerem Feld (oder einem Zeichen) erscheinen
 * "Zuletzt gesucht", "Zuletzt angesehen" und die Produktgruppen (Template #tsi-search-home aus
 * snippets/tsi-search-home.liquid), ab zwei Zeichen die Live-Treffer.
 * Live-Treffer: Shopify Predictive Search API mit Section Rendering
 * (Section: sections/predictive-search-dropdown.liquid,
 *  Inhalt: snippets/predictive-search-dropdown-results.liquid).
 * Verlauf: nur mit Zustimmung zu Praeferenzen (Shopify Customer Privacy API), nur im Browser (localStorage).
 * Oeffnen und Schliessen melden tsi:search-open und tsi:search-close (Kopf: Breite und Abdunkelung, sections/header.liquid).
 */

const SECTION_ID = 'predictive-search-dropdown';
const MIN_CHARS = 2;
const DEBOUNCE_MS = 180;
const CACHE_LIMIT = 40;
const KEY_QUERIES = 'tsi-search-recent';
const KEY_PRODUCTS = 'tsi-recent-products';
const MAX_QUERIES = 4;
const MAX_PRODUCTS = 6;
const SHOW_PRODUCTS = 3;

/* ---------- Verlauf mit Zustimmung ---------- */

let consentPromise = null;

function readConsent() {
  const cp = window.Shopify && window.Shopify.customerPrivacy;
  if (cp && typeof cp.preferencesProcessingAllowed === 'function') return !!cp.preferencesProcessingAllowed();
  return null;
}

function historyAllowed() {
  if (!consentPromise) {
    consentPromise = new Promise((resolve) => {
      const known = readConsent();
      if (known !== null) return resolve(known);
      // Ausserhalb von Shopify (lokale Vorschau) gibt es keine Zustimmungsabfrage
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return resolve(true);
      let done = false;
      const finish = (value) => {
        if (done) return;
        done = true;
        resolve(value);
      };
      try {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          finish(error ? false : readConsent() === true);
        });
      } catch (e) {
        finish(false);
      }
      setTimeout(() => finish(readConsent() === true), 2500);
    });
  }
  return consentPromise;
}

// Zustimmung geaendert: neu pruefen; ohne Zustimmung den gespeicherten Verlauf entfernen
document.addEventListener('visitorConsentCollected', () => {
  consentPromise = null;
  historyAllowed().then((ok) => {
    if (!ok) clearHistory();
  });
});

function readList(key) {
  try {
    const list = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

function writeList(key, list) {
  try {
    if (list.length) localStorage.setItem(key, JSON.stringify(list));
    else localStorage.removeItem(key);
  } catch (e) {
    /* Speicher gesperrt: Verlauf entfaellt still */
  }
}

function clearHistory() {
  writeList(KEY_QUERIES, []);
  writeList(KEY_PRODUCTS, []);
}

function cleanTerm(value) {
  return (value || '').replace(/\s+/g, ' ').trim().slice(0, 80);
}

function saveQuery(value) {
  const term = cleanTerm(value);
  if (!term) return;
  historyAllowed().then((ok) => {
    if (!ok) return;
    const lower = term.toLowerCase();
    const list = readList(KEY_QUERIES).filter((q) => typeof q === 'string' && q.toLowerCase() !== lower);
    list.unshift(term);
    writeList(KEY_QUERIES, list.slice(0, MAX_QUERIES));
  });
}

function removeQuery(term) {
  const lower = (term || '').toLowerCase();
  writeList(
    KEY_QUERIES,
    readList(KEY_QUERIES).filter((q) => typeof q === 'string' && q.toLowerCase() !== lower)
  );
}

function pathOf(url) {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch (e) {
    return '';
  }
}

function validProduct(p) {
  return (
    p &&
    typeof p.u === 'string' &&
    p.u.charAt(0) === '/' &&
    p.u.charAt(1) !== '/' &&
    typeof p.t === 'string' &&
    p.t.length > 0
  );
}

function validImage(src) {
  return typeof src === 'string' && /^(https:)?\/\/[^/]+\.(shopify\.com|shopifycdn\.net|t-s-i\.de)\//.test(src);
}

// Produktseite: dieses Produkt fuer "Zuletzt angesehen" merken
function rememberViewedProduct() {
  const node = document.getElementById('tsi-viewed-product');
  if (!node) return;
  let p = null;
  try {
    p = JSON.parse(node.textContent);
  } catch (e) {
    return;
  }
  if (!validProduct(p)) return;
  historyAllowed().then((ok) => {
    if (!ok) return;
    const path = pathOf(p.u);
    const list = readList(KEY_PRODUCTS).filter((x) => validProduct(x) && pathOf(x.u) !== path);
    list.unshift({ u: p.u, t: String(p.t).slice(0, 160), s: String(p.s || '').slice(0, 40), i: validImage(p.i) ? p.i : '' });
    writeList(KEY_PRODUCTS, list.slice(0, MAX_PRODUCTS));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', rememberViewedProduct);
} else {
  rememberViewedProduct();
}

/* ---------- Bausteine fuer das Startpanel ---------- */

const SVG_NS = 'http://www.w3.org/2000/svg';

function icon(paths, cls) {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.8');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  if (cls) svg.setAttribute('class', cls);
  paths.forEach((d) => {
    const el = document.createElementNS(SVG_NS, d.startsWith('c:') ? 'circle' : 'path');
    if (d.startsWith('c:')) {
      const [cx, cy, r] = d.slice(2).split(',');
      el.setAttribute('cx', cx);
      el.setAttribute('cy', cy);
      el.setAttribute('r', r);
    } else {
      el.setAttribute('d', d);
    }
    svg.appendChild(el);
  });
  return svg;
}

function el(tag, cls, text) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text !== undefined) node.textContent = text;
  return node;
}

class PredictiveSearchDropdown extends HTMLElement {
  connectedCallback() {
    this.searchComponent = this.closest('search-input');
    this.form = this.closest('form');
    this.panel = this.querySelector('[data-psd-panel]');
    this.input = this.searchComponent
      ? this.searchComponent.querySelector('input[type="search"]')
      : null;
    if (!this.input || !this.panel) return;

    this.searchId = this.searchComponent.id;
    this.cache = new Map();
    this.abortController = null;
    this.debounceTimer = null;
    this.lastRenderedTerm = '';
    this.mode = '';

    // A11y-Verdrahtung
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.panel.id);
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('enterkeyhint', 'search');

    // Theme-PubSub: search-input.js publiziert bei Eingabe und beim Leeren
    if (typeof subscribe === 'function') {
      this.unsubInput = subscribe(`search:input:${this.searchId}`, ({ value }) =>
        this.onInput(value)
      );
      this.unsubClear = subscribe(`search:clear:${this.searchId}`, () => this.close());
    } else {
      this.input.addEventListener('input', () => this.onInput(this.input.value));
    }

    // Fokus: gefuelltes Feld zeigt die Treffer (aus dem Cache), leeres Feld das Startpanel
    this.input.addEventListener('focus', () => this.onFocus());
    // Klick ins bereits fokussierte Feld nach Esc: Panel wieder oeffnen
    this.input.addEventListener('click', () => {
      if (!this.isOpen()) this.onFocus();
    });

    // Tastatur: Escape schliesst, Pfeiltasten navigieren durch die Eintraege
    this.input.addEventListener('keydown', (event) => this.onInputKeydown(event));
    this.panel.addEventListener('keydown', (event) => this.onPanelKeydown(event));

    // Maus im Panel: Fokus bleibt im Feld (Safari fokussiert Links beim Klick nicht, das Panel bliebe sonst nicht offen)
    this.panel.addEventListener('mousedown', (event) => {
      if (event.target.closest('a, button')) event.preventDefault();
    });
    this.panel.addEventListener('click', (event) => this.onPanelClick(event));

    // Klick ausserhalb schliesst, Submit (Enter) merkt den Begriff und schliesst
    this.onDocumentClick = (event) => {
      if (!this.isOpen()) return;
      if (this.searchComponent.contains(event.target)) return;
      // Lupe im Kopf (1024 bis 1279 px) oeffnet die Suche, der Klick darf sie nicht gleich wieder schliessen
      if (event.target.closest(`[aria-controls="${this.searchId}"]`)) return;
      this.close();
    };
    document.addEventListener('click', this.onDocumentClick);
    // Tab aus der Suche heraus schliesst
    this.searchComponent.addEventListener('focusout', (event) => {
      const next = event.relatedTarget;
      if (next && !this.searchComponent.contains(next)) this.close();
    });
    if (this.form) {
      this.form.addEventListener('submit', () => {
        saveQuery(this.input.value);
        this.close();
      });
    }
  }

  disconnectedCallback() {
    if (this.unsubInput) this.unsubInput();
    if (this.unsubClear) this.unsubClear();
    document.removeEventListener('click', this.onDocumentClick);
  }

  isOpen() {
    return this.hasAttribute('data-open');
  }

  open() {
    const wasOpen = this.isOpen();
    this.panel.hidden = false;
    this.setAttribute('data-open', '');
    this.input.setAttribute('aria-expanded', 'true');
    if (!wasOpen) this.dispatchEvent(new CustomEvent('tsi:search-open', { bubbles: true }));
  }

  close() {
    const wasOpen = this.isOpen();
    if (this.abortController) this.abortController.abort();
    clearTimeout(this.debounceTimer);
    this.removeAttribute('data-loading');
    this.panel.hidden = true;
    this.removeAttribute('data-open');
    this.input.setAttribute('aria-expanded', 'false');
    if (wasOpen) this.dispatchEvent(new CustomEvent('tsi:search-close', { bubbles: true }));
  }

  onFocus() {
    // Fokus kommt von Esc im Panel zurueck ins Feld: zu bleibt zu
    if (this.quietFocus) {
      this.quietFocus = false;
      return;
    }
    const term = this.input.value.trim();
    if (term.length >= MIN_CHARS) this.requestResults(term, true);
    else this.showHome();
  }

  onInput(value) {
    const term = (value || '').trim();
    clearTimeout(this.debounceTimer);
    if (term.length < MIN_CHARS) {
      if (this.abortController) this.abortController.abort();
      this.removeAttribute('data-loading');
      this.lastRenderedTerm = '';
      if (document.activeElement === this.input) this.showHome();
      else this.close();
      return;
    }
    this.debounceTimer = setTimeout(() => this.requestResults(term), DEBOUNCE_MS);
  }

  /* Startpanel: Verlauf (falls erlaubt) und Produktgruppen */
  showHome() {
    const tpl = document.getElementById('tsi-search-home');
    if (!tpl) {
      this.close();
      return;
    }
    const token = (this.homeToken = (this.homeToken || 0) + 1);
    const frag = tpl.content.cloneNode(true);
    const root = frag.querySelector('.psd-home');
    this.mode = 'home';
    this.panel.replaceChildren(frag);
    this.open();
    historyAllowed().then((ok) => {
      if (!ok || token !== this.homeToken || this.mode !== 'home' || !root.isConnected) return;
      const history = this.buildHistory(tpl.dataset);
      if (history) root.insertBefore(history, root.firstChild);
    });
  }

  buildHistory(t) {
    const queries = readList(KEY_QUERIES).filter((q) => typeof q === 'string' && q).slice(0, MAX_QUERIES);
    const here = pathOf(window.location.href);
    const products = readList(KEY_PRODUCTS)
      .filter((p) => validProduct(p) && pathOf(p.u) !== here)
      .slice(0, SHOW_PRODUCTS);
    if (!queries.length && !products.length) return null;

    const wrap = document.createDocumentFragment();
    let clearPlaced = false;
    const head = (label) => {
      const h = el('div', 'psd-head');
      h.appendChild(el('div', 'psd-label', label));
      if (!clearPlaced) {
        const clear = el('button', 'psd-clear', t.clear);
        clear.type = 'button';
        clear.setAttribute('data-psd-clear', '');
        h.appendChild(clear);
        clearPlaced = true;
      }
      return h;
    };

    if (queries.length) {
      const sec = el('div', 'psd-sec');
      sec.setAttribute('data-psd-recent', '');
      sec.appendChild(head(t.recent));
      const list = el('div', 'psd-list');
      queries.forEach((q) => {
        const row = el('div', 'psd-qrow');
        const a = el('a', 'psd-row psd-row--q');
        const url = new URL(t.searchUrl || '/search', window.location.origin);
        url.searchParams.set('q', q);
        url.searchParams.set('type', 'product');
        a.href = url.pathname + url.search;
        a.setAttribute('data-psd-query', q);
        const ic = el('span', 'psd-qicon');
        ic.appendChild(icon(['c:12,12,8.5', 'M12 7.5V12l3 2']));
        a.appendChild(ic);
        a.appendChild(el('span', 'psd-q', q));
        const x = el('button', 'psd-x');
        x.type = 'button';
        x.setAttribute('data-psd-remove', q);
        x.setAttribute('aria-label', `${t.remove}: ${q}`);
        x.appendChild(icon(['M7 7l10 10', 'M17 7L7 17']));
        row.appendChild(a);
        row.appendChild(x);
        list.appendChild(row);
      });
      sec.appendChild(list);
      wrap.appendChild(sec);
    }

    if (products.length) {
      const sec = el('div', 'psd-sec');
      sec.setAttribute('data-psd-viewed', '');
      sec.appendChild(head(t.viewed));
      const list = el('div', 'psd-list');
      products.forEach((p) => {
        const a = el('a', 'psd-row');
        a.href = p.u;
        const thumb = el('span', 'psd-thumb');
        if (validImage(p.i)) {
          const img = el('img', 'psd-img');
          img.src = p.i;
          img.alt = '';
          img.loading = 'lazy';
          img.decoding = 'async';
          img.width = 56;
          img.height = 56;
          img.addEventListener('error', () => img.remove());
          thumb.appendChild(img);
        }
        a.appendChild(thumb);
        const main = el('span', 'psd-main');
        main.appendChild(el('span', 'psd-title', p.t));
        if (p.s) {
          const meta = el('span', 'psd-meta');
          meta.appendChild(el('span', 'psd-sku', `${t.sku} ${p.s}`));
          main.appendChild(meta);
        }
        a.appendChild(main);
        list.appendChild(a);
      });
      sec.appendChild(list);
      wrap.appendChild(sec);
    }
    return wrap;
  }

  onPanelClick(event) {
    const remove = event.target.closest('[data-psd-remove]');
    if (remove) {
      event.preventDefault();
      removeQuery(remove.getAttribute('data-psd-remove'));
      const row = remove.closest('.psd-qrow');
      const sec = remove.closest('.psd-sec');
      if (row) row.remove();
      if (sec && !sec.querySelector('.psd-qrow')) this.showHome();
      this.input.focus();
      return;
    }
    if (event.target.closest('[data-psd-clear]')) {
      event.preventDefault();
      clearHistory();
      this.showHome();
      this.input.focus();
      return;
    }
    // Treffer oder "Alle Ergebnisse": den getippten Begriff merken
    if (this.mode === 'results' && event.target.closest('a[href]')) saveQuery(this.input.value);
    if (event.target.closest('a[data-psd-query]')) saveQuery(event.target.closest('a[data-psd-query]').getAttribute('data-psd-query'));
  }

  requestResults(term, fromFocus = false) {
    if (fromFocus && term === this.lastRenderedTerm && this.mode === 'results' && this.panel.innerHTML !== '') {
      this.open();
      return;
    }
    if (this.cache.has(term)) {
      this.render(this.cache.get(term), term);
      return;
    }
    this.fetchResults(term);
  }

  async fetchResults(term) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.setAttribute('data-loading', '');

    const params = new URLSearchParams();
    params.set('q', term);
    params.set('section_id', SECTION_ID);
    // Nur Produkte: Shopifys Query-Vorschlaege sind nicht steuerbar und werden
    // im Panel bewusst nicht angezeigt (siehe Results-Snippet).
    params.set('resources[type]', 'product');
    // 10 = API-Maximum. Liefert einen groesseren Pool, aus dem das Liquid-Snippet
    // Topseller (Tag badge:Topseller) vorzieht und max. 8 Zeilen rendert.
    params.set('resources[limit]', '10');
    // Wichtig fuer B2B: Artikelnummern (SKU) und Hersteller-Nr. (Barcode) mit durchsuchen
    params.set(
      'resources[options][fields]',
      'title,product_type,variants.title,variants.sku,variants.barcode,vendor,tag'
    );

    try {
      const response = await fetch(`${window.routes.predictive_search_url}?${params.toString()}`, {
        signal: this.abortController.signal,
      });
      if (!response.ok) throw new Error(`Predictive search: HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = doc.querySelector('[data-psd-content]');
      const markup = content ? content.innerHTML : '';

      if (this.cache.size >= CACHE_LIMIT) this.cache.delete(this.cache.keys().next().value);
      this.cache.set(term, markup);

      // Nur rendern, wenn der Begriff noch dem aktuellen Eingabewert entspricht
      if (term === this.input.value.trim()) this.render(markup, term);
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Predictive search error:', error);
    } finally {
      this.removeAttribute('data-loading');
    }
  }

  render(markup, term) {
    this.lastRenderedTerm = term;
    if (!markup || markup.trim() === '') {
      this.close();
      return;
    }
    this.mode = 'results';
    this.panel.innerHTML = markup;
    const scroller = this.panel.querySelector('.psd-scroll');
    if (scroller) scroller.scrollTop = 0;
    this.open();
  }

  // Pfeiltasten: Zeilen und Treffer, nicht die kleinen Entfernen- und Loeschen-Knoepfe (die erreicht Tab)
  focusableItems() {
    return Array.from(this.panel.querySelectorAll('a[href], button:not(.psd-x):not(.psd-clear)'));
  }

  onInputKeydown(event) {
    if (event.key === 'Escape' && this.isOpen()) {
      event.stopPropagation();
      this.close();
      return;
    }
    if (event.key === 'ArrowDown' && this.isOpen()) {
      const items = this.focusableItems();
      if (items.length) {
        event.preventDefault();
        items[0].focus();
      }
    }
  }

  onPanelKeydown(event) {
    const items = this.focusableItems();
    const index = items.indexOf(document.activeElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      this.quietFocus = true;
      this.input.focus();
      this.quietFocus = false;
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = Math.min(index + 1, items.length - 1);
      if (items[next]) items[next].focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (index <= 0) {
        this.input.focus();
      } else {
        items[index - 1].focus();
      }
    }
  }
}

if (!customElements.get('predictive-search-dropdown')) {
  customElements.define('predictive-search-dropdown', PredictiveSearchDropdown);
}
